import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import { Store } from '@ngrx/store';
import { tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { AppState } from '../../../_store/app-state.model';
import { AppSelectors } from '../../../_store/selector-types';
import { IAuthenticationInfo } from '../../models/models-index';
import { RuntimeConfigService } from '../config/runtime-config.service';

export interface IPropDictionary {
  [key: string]: string | number | boolean;
}

export interface IMeasurementDictionary {
  [key: string]: number;
}

export interface TelemetryContext {
  RoutePath?: string;
  RouteUrl?: string;
  RouteTitle?: string;
  RouteId?: string;
  RouteCategory?: string;
  ReportKind?: 'CustomReport' | 'PrebuiltReport' | 'Unknown';
  ReportName?: string;
  ReportId?: string;
  ReportType?: string;
  DataSetName?: string;
  PanelName?: string;
  VisualizationName?: string;
}

export interface ReportExportTelemetryContext extends TelemetryContext {
  ExportFormat: 'CSV' | 'Excel' | 'PDF';
  RowCount?: number;
  ColumnCount?: number;
}

export interface ReportFilterTelemetryContext extends TelemetryContext {
  FilterName?: string;
  FilterLabel?: string;
  FilterType?: string;
  FilterCount?: number;
  SelectionCount?: number;
  IsLocked?: boolean;
}

export interface IAppInsightService {
  trackPageView(options?: { pageName?: string, url?: string, props?: IPropDictionary, measurements?: IMeasurementDictionary }): void;
  trackEvent(eventName: string, options?: { props?: IPropDictionary, measurements?: IMeasurementDictionary, routeInfo?: ActivatedRouteSnapshot }): void;
  trackException(ex: Error, options?: { handledAt?: string, props?: IPropDictionary, measurements?: IMeasurementDictionary }): void;
  trackTrace(message: string, options?: { props?: IPropDictionary, measurements?: IMeasurementDictionary }): void;
}

@Injectable({ providedIn: 'root'})
export class AppInsightsService implements IAppInsightService {
  private telemetry?: ApplicationInsights;
  private applicationHostDomain: string;
  private user: IAuthenticationInfo;
  private currentRouteContext: TelemetryContext = {};
  private authenticatedUserId?: string;

  constructor(
    private store: Store<AppState>,
    private location: Location,
    private runtimeConfigService: RuntimeConfigService
  ) {
    this.clearLegacyBuffer();
    this.initializeTelemetry();
    this.installLegacyGlobalFacade();

    this.store.select(AppSelectors.selectLoggedInUser)
      .pipe(tap(user => this.setUserContext(user)))
      .subscribe();
  }

  trackPageView(options?: { pageName?: string, url?: string, props?: IPropDictionary, measurements?: IMeasurementDictionary }): void {
    const event = options || {};
    const pageName = event.pageName || this.getDefaultPageName();
    const url = event.url || this.getDefaultUrl();
    const props = this.getProps(event.props);

    this.telemetry?.trackPageView({
      name: pageName,
      uri: url,
      properties: props,
      measurements: event.measurements || {}
    });
  }

  trackRoutePageView(routeData: any, url: string, routePath?: string): void {
    const routeContext = this.buildRouteContext(routeData, url, routePath);
    this.currentRouteContext = routeContext;

    this.trackPageView({
      pageName: 'Page Viewed',
      url,
      props: routeContext as IPropDictionary
    });
  }

  trackEvent(
    eventName: string,
    options?: {
      props?: IPropDictionary,
      measurements?: IMeasurementDictionary,
      routeInfo?: ActivatedRouteSnapshot
    }
  ): void {
    const props = this.getProps(options?.props, options?.routeInfo);
    const measurements = this.getMeasurements(options?.measurements);

    this.trackEventInternal(eventName, props, measurements);

    const loginProps = this.getLoginSucceededProps(eventName, props);
    if (loginProps) {
      this.trackEventInternal('Login Succeeded', loginProps, measurements);
    }
  }

  trackException(
    ex: Error,
    options?: { handledAt?: string, props?: IPropDictionary, measurements?: IMeasurementDictionary }
  ): void {
    const props = this.getProps({
      ...(options?.props || {}),
      HandledAt: options?.handledAt || ''
    });

    this.telemetry?.trackException({
      exception: ex,
      properties: props,
      measurements: options?.measurements || {}
    });
  }

  trackTrace(message: string, options?: { props?: IPropDictionary, measurements?: IMeasurementDictionary }): void {
    this.telemetry?.trackTrace({
      message,
      severityLevel: SeverityLevel.Warning,
      properties: this.getProps(options?.props),
      measurements: options?.measurements || {}
    });
  }

  trackReportExported(context: ReportExportTelemetryContext): void {
    this.trackProductEvent('Report Exported', context, {
      RowCount: context.RowCount || 0,
      ColumnCount: context.ColumnCount || 0
    });
  }

  trackReportFilterOpened(context: ReportFilterTelemetryContext): void {
    this.trackProductEvent('Report Filter Opened', context);
  }

  trackReportFilterApplied(context: ReportFilterTelemetryContext): void {
    this.trackProductEvent('Report Filter Applied', context, {
      FilterCount: context.FilterCount || 0,
      SelectionCount: context.SelectionCount || 0
    });
  }

  trackReportFilterRemoved(context: ReportFilterTelemetryContext): void {
    this.trackProductEvent('Report Filter Removed', context);
  }

  trackReportFiltersReset(context: ReportFilterTelemetryContext): void {
    this.trackProductEvent('Report Filters Reset', context, {
      FilterCount: context.FilterCount || 0
    });
  }

  trackReportFilterLockToggled(context: ReportFilterTelemetryContext): void {
    this.trackProductEvent('Report Filter Lock Toggled', context);
  }

  trackReportIndexChanged(context: TelemetryContext & { PreviousIndex?: string, SelectedIndex?: string }): void {
    this.trackProductEvent('Report Index Changed', context);
  }

  trackVisualizationMetricChanged(context: TelemetryContext & { PreviousMetric?: string, SelectedMetric?: string, MetricIndex?: number }): void {
    this.trackProductEvent('Visualization Metric Changed', context, {
      MetricIndex: context.MetricIndex || 0
    });
  }

  trackCustomReportBuilderStepViewed(context: TelemetryContext & { StepId?: string, StepName?: string }): void {
    this.trackProductEvent('Custom Report Builder Step Viewed', context);
  }

  trackCustomReportBuilderDatasetSelected(context: TelemetryContext): void {
    this.trackProductEvent('Custom Report Builder Dataset Selected', context);
  }

  trackCustomReportBuilderReportTypeSelected(context: TelemetryContext): void {
    this.trackProductEvent('Custom Report Builder Report Type Selected', context);
  }

  trackCustomReportSaved(context: TelemetryContext): void {
    this.trackProductEvent('Custom Report Saved', context);
  }

  trackCustomReportDeleted(context: TelemetryContext): void {
    this.trackProductEvent('Custom Report Deleted', context);
  }

  flush(): void {
    this.telemetry?.flush();
  }

  setHostDomain(host: string) {
    this.applicationHostDomain = host;
  }

  private initializeTelemetry(): void {
    const runtimeConfig = this.runtimeConfigService.config;
    const connectionString = runtimeConfig.applicationInsightsConnectionString;
    const instrumentationKey = runtimeConfig.instrumentationKey || environment.applicationInsights.instrumentationKey;

    if (!connectionString && !instrumentationKey) {
      return;
    }

    this.telemetry = new ApplicationInsights({
      config: {
        connectionString: connectionString || undefined,
        instrumentationKey: connectionString ? undefined : instrumentationKey,
        enableAutoRouteTracking: false,
        enableSessionStorageBuffer: false,
        maxBatchSizeInBytes: 64 * 1024,
        maxBatchInterval: 5000,
        isBeaconApiDisabled: false,
        disableExceptionTracking: true,
        enableUnhandledPromiseRejectionTracking: true
      }
    });

    this.telemetry.addTelemetryInitializer(item => {
      const baseData = (item as any).baseData || (item as any).data?.baseData;
      if (!baseData) {
        return;
      }

      baseData.properties = {
        ...this.getDefaultProps(),
        ...this.stringifyProps(this.currentRouteContext as IPropDictionary),
        ...(baseData.properties || {})
      };
    });

    this.telemetry.loadAppInsights();

    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this.flush());
    }
  }

  private installLegacyGlobalFacade(): void {
    if (typeof window === 'undefined') {
      return;
    }

    (window as any).appInsights = {
      trackException: (ex: Error, handledAt?: string, props?: any, measurements?: any) =>
        this.trackException(ex, { handledAt, props, measurements }),
      trackEvent: (name: string, props?: any, measurements?: any) =>
        this.trackEvent(name, { props, measurements }),
      trackTrace: (message: string, props?: any) =>
        this.trackTrace(message, { props }),
      trackPageView: (name?: string, url?: string, props?: any, measurements?: any) =>
        this.trackPageView({ pageName: name, url, props, measurements }),
      flush: () => this.flush()
    };
  }

  private trackProductEvent(eventName: string, context: Record<string, any>, measurements?: IMeasurementDictionary): void {
    const props = this.contextToProps(context);
    this.trackEventInternal(eventName, this.getProps(props), this.getMeasurements(measurements));
  }

  private trackEventInternal(eventName: string, props: IPropDictionary, measurements?: IMeasurementDictionary): void {
    this.telemetry?.trackEvent({
      name: eventName,
      properties: props,
      measurements: measurements || {}
    });
  }

  private setUserContext(user: IAuthenticationInfo): void {
    this.user = user;

    if (!this.telemetry) {
      return;
    }

    const stableUserId = this.sanitizeUserContextId(user?.userId || user?.userName || '');
    if (!stableUserId) {
      if (this.authenticatedUserId) {
        this.telemetry.clearAuthenticatedUserContext();
        this.authenticatedUserId = undefined;
      }
      return;
    }

    if (stableUserId !== this.authenticatedUserId) {
      const accountId = this.sanitizeUserContextId(user.roleEntity || '');
      this.telemetry.setAuthenticatedUserContext(stableUserId, accountId || undefined, true);
      this.authenticatedUserId = stableUserId;
    }
  }

  private getProps(props?: IPropDictionary, routeInfo?: ActivatedRouteSnapshot): IPropDictionary {
    const defaults = this.getDefaultProps();

    if (routeInfo) {
      if (routeInfo.routeConfig?.path) {
        defaults.RoutePath = routeInfo.routeConfig.path;
      }
      if (routeInfo.data?.title) {
        defaults.RouteTitle = String(routeInfo.data.title);
      }
      Object.entries(routeInfo.params || {}).forEach(([key, value]) => {
        defaults['PathParam_' + key] = String(value);
      });
    }

    return {
      ...defaults,
      ...this.stringifyProps(this.currentRouteContext as IPropDictionary),
      ...this.stringifyProps(props || {})
    };
  }

  private getDefaultProps(): IPropDictionary {
    const config = this.runtimeConfigService.config;
    const defaults: IPropDictionary = {
      App: environment.appName,
      Client: config.client || environment.client || '',
      Environment: config.env || environment.environmentName || '',
      Host: this.applicationHostDomain || config.webUri || environment.hostName || '',
      UserName: '',
      ClientUserName: '',
      FirstName: '',
      LastName: '',
      UserRole: '',
      RoleEntity: ''
    };

    if (this.user) {
      defaults.UserName = this.user.userName || '';
      defaults.ClientUserName = this.user.clientUserName || '';
      defaults.FirstName = this.user.firstName || '';
      defaults.LastName = this.user.lastName || '';
      defaults.UserRole = this.user.role || '';
      defaults.RoleEntity = this.user.roleEntity || '';
    }

    return defaults;
  }

  private getMeasurements(measurements?: IMeasurementDictionary): IMeasurementDictionary {
    return measurements || {};
  }

  private getLoginSucceededProps(eventName: string, props: IPropDictionary): IPropDictionary | null {
    const successEvents = ['LoginSuccess', 'B2CLoginSuccess', 'SsoLoginSuccess'];
    if (!successEvents.includes(eventName)) {
      return null;
    }

    return {
      ...props,
      AuthProvider: this.inferAuthProvider(eventName, props),
      SourceEvent: eventName
    };
  }

  private inferAuthProvider(eventName: string, props: IPropDictionary): string {
    if (props.AuthProvider) {
      return String(props.AuthProvider);
    }
    if (eventName === 'B2CLoginSuccess') {
      return 'AzureB2C';
    }
    if (eventName === 'SsoLoginSuccess') {
      return 'ClientSSO';
    }
    if (props.Source === 'UserService' || props.Source === 'ClaimsFallback') {
      return 'UnifiedToken';
    }
    return 'LegacyCredentials';
  }

  private buildRouteContext(routeData: any, url: string, routePath?: string): TelemetryContext {
    const isCustomReport = url.startsWith('/custom-reports');
    const reportName = routeData?.reportName || routeData?.title || routeData?.id || '';
    const reportKind = isCustomReport
      ? 'CustomReport'
      : routeData?.reportName
        ? 'PrebuiltReport'
        : 'Unknown';

    return {
      RoutePath: routePath || '',
      RouteUrl: url,
      RouteTitle: routeData?.title || '',
      RouteId: routeData?.id || '',
      RouteCategory: routeData?.category || '',
      ReportKind: reportKind,
      ReportName: isCustomReport ? this.getCustomReportRouteName(url) : reportName,
      ReportId: isCustomReport ? this.getCustomReportId(url) : '',
      ReportType: isCustomReport ? this.getCustomReportRouteType(url) : (routeData?.reportName ? 'Prebuilt' : ''),
      DataSetName: routeData?.dataSetName || routeData?.dataset || ''
    };
  }

  private getCustomReportRouteName(url: string): string {
    if (url.includes('/custom-reports/list')) return 'Custom Reports List';
    if (url.includes('/custom-reports/create')) return 'Custom Report Builder';
    if (url.includes('/edit')) return 'Custom Report Editor';
    return 'Custom Report Viewer';
  }

  private getCustomReportRouteType(url: string): string {
    if (url.includes('/custom-reports/list')) return 'List';
    if (url.includes('/custom-reports/create')) return 'Builder';
    if (url.includes('/edit')) return 'Editor';
    return 'Viewer';
  }

  private getCustomReportId(url: string): string {
    const match = url.match(/^\/custom-reports\/([^/?#]+)/);
    const id = match?.[1] || '';
    return ['list', 'create'].includes(id) ? '' : id;
  }

  private contextToProps(context: Record<string, any>): IPropDictionary {
    const props: IPropDictionary = {};
    Object.entries(context || {}).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }
      if (typeof value === 'number' || typeof value === 'boolean') {
        props[key] = value;
        return;
      }
      props[key] = String(value);
    });
    return props;
  }

  private stringifyProps(props: IPropDictionary): IPropDictionary {
    return Object.entries(props || {}).reduce((acc, [key, value]) => {
      if (value === undefined || value === null) {
        return acc;
      }
      acc[key] = typeof value === 'object' ? JSON.stringify(value) : value;
      return acc;
    }, {} as IPropDictionary);
  }

  private sanitizeUserContextId(value: string): string {
    return (value || '').replace(/[,;=|\s]+/g, '_');
  }

  private getDefaultPageName(): string {
    return 'Page Viewed';
  }

  private getDefaultUrl(): string {
    let path = this.location.path() || '';
    const idx = path.lastIndexOf('/');
    if (idx === path.length - 1) {
      path = path.substring(0, path.length - 1);
    }
    return path;
  }

  private clearLegacyBuffer(): void {
    try { sessionStorage.removeItem('AI_buffer'); } catch {}
  }
}
